import React, { useState, useEffect } from 'react'
import { Link } from 'gatsby'
import Icon from 'src/components/ui/Icon'
import { useMenu } from 'src/graphql/queries/menu'
import type { Level1 } from 'src/graphql/queries/menu'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import FlexibleLink from 'src/components/ui/FlexibleLink/FlexibleLink'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import type { PageTypeResult } from 'src/hooks/usePageType'

import DropdownMenu from './DropdownMenu'
import DropdownMenuGender from './DropdownMenuGender'

type MenuProps = {
  menu_hierarchy: string
  menu_level: number
  menu_item_name: string
}

export const handleMenuItemClick = (
  menuSelected: MenuProps,
  pageData: PageTypeResult
) => {
  dispatchAmplitudeEvent({
    eventName: 'Menu Item Selected',
    eventData: {
      ...getStandardObject(pageData),
      ...menuSelected,
    },
  })
}

function Menu() {
  const [inHovering, setInHovering] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)
  const [fallbackDepartments, setFallbackDepartments] = useState<
    Level1[] | null
  >(null)

  const menuData = useMenu()
  const { pageData } = useGlobalContext()

  const cmsDepartments =
    menuData.allCmsHome?.nodes?.[0]?.sections?.[0]?.data?.departments

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (cmsDepartments?.length) {
      return
    }

    import('./data/menuDataFallback').then(({ menuDataFallback }) => {
      setFallbackDepartments(menuDataFallback.departments)
    })
  }, [cmsDepartments])

  const { institutionalLinksDesktop } =
    menuData.allCmsHome?.nodes?.[0]?.sections?.[0]?.data

  const departmentsData = cmsDepartments?.length
    ? cmsDepartments
    : fallbackDepartments

  if (!departmentsData) {
    return null
  }

  const [sports, ...departments] = departmentsData

  const handleMouseEnterWithDelay = (value: boolean) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    const id = setTimeout(() => {
      setInHovering(value)
    }, 300)

    setTimeoutId(id)
  }

  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    setInHovering(false)
  }

  return (
    <div
      className="relative w-full bg-white hidden restructure-small-desktop:flex flex-row basis-full py-6"
      data-testid="menu-desktop"
    >
      <nav
        className={`gap-[70px] justify-between w-full  flex flex-row basis-full max-h-[48px] dropdown-container ${
          inHovering ? 'in--hovering' : ''
        } max-w-[1366px]`}
      >
        <ul
          className="flex flex-1 flex-row justify-between items-center"
          onMouseLeave={handleMouseLeave}
        >
          <li
            onMouseEnter={() => {
              handleMouseEnterWithDelay(true)
            }}
            className="group h-full  text-center xl:pl-[6px] justify-start flex items-center pr-6 "
            data-testid="item-menu"
          >
            {sports.url && sports.url !== '/#' ? (
              <Link
                to={sports.url ?? ''}
                className="desktop-body-regular-text3 text-restructure-action text-center flex gap-2 items-center whitespace-nowrap border-b-2 border-solid border-transparent group-hover:border-restructure-background-action-1 group-hover:text-restructure-action"
                aria-label={sports.title}
                title={sports.title}
              >
                <Icon
                  name="Sports_basketball"
                  width={14}
                  height={14}
                  className="text-restructure-action"
                />
                {sports.title}
              </Link>
            ) : (
              <span
                className="pointer-events-none cursor-not-allowed desktop-body-regular-text3 text-restructure-action text-center flex gap-2 items-center whitespace-nowrap border-b-2 border-solid border-transparent group-hover:border-restructure-background-action-1 group-hover:text-restructure-action"
                aria-label={sports.title}
                title={sports.title}
                role="button"
                tabIndex={0}
              >
                <Icon
                  name="Sports_basketball"
                  width={14}
                  height={14}
                  className="text-restructure-action"
                />
                {sports.title}
              </span>
            )}

            {(!isMounted || (!!sports.categories?.length && inHovering)) && (
              <DropdownMenu
                {...sports}
                variant="second"
                setInHovering={setInHovering}
              />
            )}
          </li>
          {departments.map((department) => {
            const { title, url, categories } = department ?? {}
            const hasChildrenCategories = Boolean(categories?.length)

            return (
              <li
                key={title}
                className="flex-1 min-w-min h-full flex items-center justify-center text-center transition-all duration-75 group px-2 restructure-small-desktop:px-3"
                onMouseEnter={() => {
                  handleMouseEnterWithDelay(hasChildrenCategories)
                }}
                data-testid="item-menu"
              >
                <Link
                  to={url ?? ''}
                  className={`border-b-2 border-solid border-transparent ${
                    title === 'Outlet' || title === 'Liquidação'
                      ? 'text-restructure-error group-hover:border-restructure-background-error2'
                      : 'text-restructure-primary border-b-2 border-solid border-transparent group-hover:border-restructure-background-primary-inverted'
                  } desktop-body-regular-text3 text-center min-w-max `}
                  aria-label={title}
                  title={title}
                  onClick={() => {
                    handleMenuItemClick(
                      {
                        menu_level: 1,
                        menu_item_name: title ?? '',
                        menu_hierarchy: title ?? '',
                      },
                      pageData
                    )
                  }}
                >
                  {title}
                </Link>
                {(!isMounted || (hasChildrenCategories && inHovering)) && (
                  <DropdownMenuGender
                    {...department}
                    urlDepartment={department.url}
                    setInHovering={setInHovering}
                  />
                )}
              </li>
            )
          })}
        </ul>
        <ul className="flex flex-row gap-6 items-center">
          {!!institutionalLinksDesktop?.length &&
            institutionalLinksDesktop.map((link, index) => {
              return (
                <FlexibleLink
                  key={`${link?.label}-${index}`}
                  linkUrl={link?.href}
                  classNames="desktop-caption-regular text-restructure-secondary hover:text-restructure-action"
                  aria-label={link?.label}
                  title={link?.label}
                  data-testid="item-institutional"
                  target={link?.target}
                >
                  {link.label}
                </FlexibleLink>
              )
            })}
        </ul>
      </nav>
    </div>
  )
}

export default React.memo(Menu)
